import {CENTRAL_QUARTER_PREFIX, COMMON_BUILDING_TOKENS} from '../constants/tokens';
import {NEUTRAL_PLAYER_COLOR} from '../constants/players';
import {getCityHexKeys} from '../utils/gameState/getCityHexKeys';
import {getNeighborHexKeys} from '../utils/coords/getNeighborHexKeys';
import {checkIfNeutralPlayer} from '../utils/players/checkIfNeutralPlayer';
import {resolveFactionSkinIconPath} from '../utils/factions/resolveFactionSkinIconPath';

/**
 * Чистые расчеты облета захваченных городов (см. CityCaptureAnimation): что
 * показывать, в каком порядке, какими цветами и каким кадром камеры. Без
 * Phaser — проверяются тестами напрямую
 */

/** Иконка владельца над центральным кварталом — та же, что в модалке захвата */
export const getCityCaptureOwnerIconPath = (player) => resolveFactionSkinIconPath(
  checkIfNeutralPlayer(player?.id) ? NEUTRAL_PLAYER_COLOR : player?.factionId,
  player?.selectedSkin
);

/**
 * Шаги облета — по шагу на захваченный город. Прожигание начинается с ячейки
 * отряда захватчика на квартале города (по правилам захвата он там стоит, см.
 * checkCityCapture); не нашелся — с центрального квартала
 * @param {Object[]} capturedCities - gameState.lastCapturedCities
 * @param {Object[]} units
 * @returns {Object[]}
 */
export const buildCityCaptureSteps = (capturedCities, units) => (capturedCities || [])
  .filter((capture) => capture?.city && capture.fromPlayer && capture.toPlayer)
  .map((capture) => {
    const {city, fromPlayer, toPlayer} = capture;
    const centralQuarterHexKey = capture.centralQuarterHexKey || city.centralQuarter;
    const quarterHexKeys = [city.centralQuarter, ...(city[COMMON_BUILDING_TOKENS.QUARTER] || [])];
    const conquerorUnit = (units || []).find((unit) => (
      unit.ownerId === toPlayer.id && quarterHexKeys.includes(unit.hexKey)
    ));

    return {
      cityHexKeys: [...getCityHexKeys(city)],
      cityWinPoints: Number(capture.cityWinPoints) || 0,
      centralQuarterHexKey,
      originHexKey: conquerorUnit?.hexKey || centralQuarterHexKey,
      fromColor: fromPlayer.color,
      toColor: toPlayer.color,
      fromIconPath: getCityCaptureOwnerIconPath(fromPlayer),
      toIconPath: getCityCaptureOwnerIconPath(toPlayer),
    };
  });

/**
 * Порядок облета: от города, ближайшего к текущему кадру, каждый раз к
 * ближайшему из оставшихся — камера не мечется через все поле туда-обратно
 * @param {Object[]} steps
 * @param {{x: number, y: number}} startPoint
 * @param {function(Object): ({x: number, y: number}|null)} getStepCenter
 */
export const orderCityCaptureSteps = (steps, startPoint, getStepCenter) => {
  const remaining = steps.map((step) => ({step, center: getStepCenter(step)}));
  const ordered = [];
  let point = startPoint;
  while (remaining.length > 0) {
    let bestIndex = 0;
    let bestDistance = Infinity;
    for (let index = 0; index < remaining.length; index += 1) {
      const {center} = remaining[index];
      const distance = center && point ? Math.hypot(center.x - point.x, center.y - point.y) : 0;
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    }
    const [next] = remaining.splice(bestIndex, 1);
    ordered.push(next.step);
    point = next.center || point;
  }
  return ordered;
};

/**
 * Подмена для рендерера (HexGridRenderer.setCityCaptureOverrides): города
 * `pendingSteps`, до которых облет еще не дошел, на поле все еще прежнего
 * владельца — и в границе, и флагом центрального квартала. У текущего города
 * флаг прежнего владельца держится, пока не сменилась иконка
 * (`isCurrentTokenSwapped`), а его ячейки и соседей обычная граница не рисует
 * (`hiddenBorderHexKeys`) — их рисуют слои анимации
 */
export const buildCityCaptureOverrides = ({
  pendingSteps = [], currentStep = null, isCurrentTokenSwapped = false, hiddenBorderHexKeys = [],
}) => {
  const ownerColorByHexKey = new Map();
  const tokenByHexKey = new Map();
  pendingSteps.forEach((step) => {
    step.cityHexKeys.forEach((hexKey) => ownerColorByHexKey.set(hexKey, step.fromColor));
    tokenByHexKey.set(step.centralQuarterHexKey, `${CENTRAL_QUARTER_PREFIX}${step.fromColor}`);
  });
  if (currentStep && !isCurrentTokenSwapped) {
    tokenByHexKey.set(currentStep.centralQuarterHexKey, `${CENTRAL_QUARTER_PREFIX}${currentStep.fromColor}`);
  }
  return {ownerColorByHexKey, tokenByHexKey, hiddenBorderHexKeys: new Set(hiddenBorderHexKeys)};
};

/**
 * Владельцы ячеек для двух слоев границы текущего города: «после» — как на
 * настоящем поле (с городами, до которых облет еще не дошел, у прежних
 * владельцев), «до» — то же, но и сам город еще у прежнего владельца
 * @param {Map<string, string>} baseOwnerColorByHexKey - настоящие владельцы
 * @param {Object} step
 * @param {Object[]} pendingSteps - города после текущего
 */
export const buildCityCaptureLayerOwners = (baseOwnerColorByHexKey, step, pendingSteps = []) => {
  const after = new Map(baseOwnerColorByHexKey);
  pendingSteps.forEach((pendingStep) => {
    pendingStep.cityHexKeys.forEach((hexKey) => after.set(hexKey, pendingStep.fromColor));
  });
  const before = new Map(after);
  step.cityHexKeys.forEach((hexKey) => before.set(hexKey, step.fromColor));
  return {before, after};
};

/**
 * Ячейки, чья граница меняется вместе с городом: сам город и соседние ячейки
 * владений (у соседа меняется сторона к городу — например, между городом и
 * владениями захватчика граница пропадает)
 */
export const collectCityCaptureAffectedHexKeys = (step, before, after) => {
  const hexKeys = new Set(step.cityHexKeys);
  step.cityHexKeys.forEach((hexKey) => {
    getNeighborHexKeys(hexKey).forEach((neighborKey) => {
      if (before.has(neighborKey) || after.has(neighborKey)) {
        hexKeys.add(neighborKey);
      }
    });
  });
  return [...hexKeys];
};

/** Границы набора ячеек по их вершинам */
export const calcHexListBounds = (hexList) => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  hexList.forEach((hex) => {
    (hex.points || []).forEach((point) => {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    });
  });
  if (!Number.isFinite(minX)) {
    return null;
  }
  return {minX, maxX, minY, maxY, centerX: (minX + maxX) / 2, centerY: (minY + maxY) / 2};
};

/**
 * Кадр камеры над городом: центр города — в центр свободной области, зум —
 * «средний» (preferredZoom), а если город туда не влезает долей fitShare, —
 * дальше, но не дальше minZoom
 * @param {Object[]} hexList - ячейки города (из рендерера)
 * @param {{width: number, height: number}} freeAreaPx - свободная область в пикселях канвы
 * @param {{preferredZoom: number, minZoom: number, fitShare: number}} config
 * @returns {{x: number, y: number, zoom: number}|null}
 */
export const calcCityCaptureCameraFrame = (hexList, freeAreaPx, {preferredZoom, minZoom, fitShare}) => {
  const bounds = calcHexListBounds(hexList);
  if (!bounds) {
    return null;
  }
  const width = Math.max(1, bounds.maxX - bounds.minX);
  const height = Math.max(1, bounds.maxY - bounds.minY);
  const fitZoom = Math.min(
    (freeAreaPx.width * fitShare) / width,
    (freeAreaPx.height * fitShare) / height
  );
  const zoom = Math.max(minZoom, Math.min(preferredZoom, Number.isFinite(fitZoom) ? fitZoom : preferredZoom));
  return {x: bounds.centerX, y: bounds.centerY, zoom};
};

/**
 * Кадр камеры не уходит за игровую часть поля дальше, чем на padding: если
 * свободная область при зуме кадра шире игровой части — центр по этой оси
 * встает в ее середину
 * @param {{x: number, y: number, zoom: number}} frame
 * @param {{width: number, height: number}} freeAreaPx
 * @param {{centerX: number, centerY: number, width: number, height: number}|null} bounds - игровая часть
 * @param {number} padding - в единицах мира
 */
export const clampCameraFrameToBounds = (frame, freeAreaPx, bounds, padding = 0) => {
  if (!frame || !bounds) {
    return frame;
  }
  const clampAxis = (value, center, size, viewSize) => {
    const halfView = viewSize / frame.zoom / 2;
    const min = center - size / 2 - padding + halfView;
    const max = center + size / 2 + padding - halfView;
    return min > max ? center : Math.min(max, Math.max(min, value));
  };
  return {
    ...frame,
    x: clampAxis(frame.x, bounds.centerX, bounds.width, freeAreaPx.width),
    y: clampAxis(frame.y, bounds.centerY, bounds.height, freeAreaPx.height),
  };
};

/**
 * Рваный край фронта прожигания: точки окружности радиуса radius, радиус
 * качается по углу (две волны разной частоты) и во времени (wobble)
 * @returns {{x: number, y: number}[]}
 */
export const buildRaggedRingPoints = (center, radius, amplitude, count, wobble) => (
  Array.from({length: count}, (_, index) => {
    const angle = (index / count) * Math.PI * 2;
    const shift = amplitude * (0.6 * Math.sin(5 * angle + wobble) + 0.4 * Math.sin(11 * angle - 1.7 * wobble));
    const pointRadius = Math.max(0, radius + shift);
    return {x: center.x + Math.cos(angle) * pointRadius, y: center.y + Math.sin(angle) * pointRadius};
  })
);

/**
 * Радиус круга прожигания: от чуть больше ячейки захватчика до самой дальней
 * вершины города с запасом
 */
export const calcCityCaptureBurnRadii = (originHex, hexList, {startRadiusRatio, endPaddingRatio}) => {
  let farthest = 0;
  hexList.forEach((hex) => {
    (hex.points || []).forEach((point) => {
      farthest = Math.max(farthest, Math.hypot(point.x - originHex.centerX, point.y - originHex.centerY));
    });
  });
  const start = originHex.size * startRadiusRatio;
  return {start, end: Math.max(start, farthest + originHex.size * endPaddingRatio)};
};

/**
 * Звук прожигания города по его очкам: первая строка burnSounds, в которую
 * очки укладываются включительно (чем крупнее город, тем мощнее звук)
 * @param {number} cityWinPoints
 * @param {{maxCityWinPoints: number, soundId: string}[]} burnSounds
 * @returns {string|null}
 */
export const getCityCaptureBurnSoundId = (cityWinPoints, burnSounds) => {
  const points = Number(cityWinPoints) || 0;
  const matched = (burnSounds || []).find(({maxCityWinPoints}) => points <= maxCityWinPoints);
  return matched ? matched.soundId : null;
};

/** Мягкий разгон и торможение (Sine in-out) */
export const easeInOutSine = (t) => 0.5 - Math.cos(Math.PI * Math.min(1, Math.max(0, t))) / 2;

/** Кадр камеры между двумя кадрами: центр и зум — линейно по `t` */
export const interpolateCameraFrame = (from, to, t) => ({
  x: from.x + (to.x - from.x) * t,
  y: from.y + (to.y - from.y) * t,
  zoom: from.zoom + (to.zoom - from.zoom) * t,
});

/** Доля смены иконок на доле прожигания `burnShare`: 0 — прежняя, 1 — новая */
export const calcCityCaptureIconSwapShare = (burnShare, {iconSwapStartShare, iconSwapEndShare}) => {
  if (burnShare <= iconSwapStartShare) {
    return 0;
  }
  if (burnShare >= iconSwapEndShare) {
    return 1;
  }
  return (burnShare - iconSwapStartShare) / (iconSwapEndShare - iconSwapStartShare);
};
